import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { IconButton } from './IconButton';

// Okno modalne.
//
// Poprzednia wersja renderowała się w miejscu wywołania i obsługiwała wyłącznie
// kliknięcie w ikonę zamknięcia. Brakowało rzeczy, których użytkownik oczekuje
// odruchowo i które mają znaczenie przy pracy z klawiaturą:
//  - zamykanie klawiszem Escape,
//  - zamykanie kliknięciem w tło,
//  - pułapka focusu (Tab nie ucieka do elementów pod spodem),
//  - przywrócenie focusu na element, z którego modal otwarto,
//  - blokada przewijania strony pod modalem,
//  - portal do <body>, więc modal nie jest przycinany przez kontenery z overflow.

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
  closeOnBackdrop = true,
}) {
  const panelRef = useRef(null);
  const previouslyFocused = useRef(null);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = panelRef.current?.querySelectorAll(FOCUSABLE);
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      // Zapętlenie focusu: z ostatniego elementu Tab wraca na pierwszy,
      // a Shift+Tab z pierwszego przeskakuje na ostatni.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    previouslyFocused.current = document.activeElement;

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    // Focus na pierwszym polu formularza, żeby dało się od razu pisać.
    // requestAnimationFrame, bo w momencie efektu panel jest już w DOM,
    // ale jeszcze nie po malowaniu — focus przed nim potrafi przewinąć stronę.
    const frame = requestAnimationFrame(() => {
      const focusable = panelRef.current?.querySelectorAll(FOCUSABLE);
      if (focusable?.length) focusable[0].focus();
      else panelRef.current?.focus();
    });

    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = overflow;
      // Powrót focusu tam, skąd modal otwarto — inaczej po zamknięciu
      // focus ląduje na <body> i nawigacja klawiaturą zaczyna się od nowa.
      if (previouslyFocused.current instanceof HTMLElement) {
        previouslyFocused.current.focus();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/60 p-4 backdrop-blur-sm animate-fade-in sm:items-center sm:p-6"
      onMouseDown={(event) => {
        // onMouseDown, a nie onClick: przy zaznaczaniu tekstu w modalu i puszczeniu
        // przycisku poza nim onClick zamknąłby okno w trakcie zaznaczania.
        if (closeOnBackdrop && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={cn(
          'my-auto w-full animate-scale-in rounded-lg border border-line bg-surface shadow-pop outline-none',
          SIZES[size]
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-3.5">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-content">{title}</h2>
            {description && <p className="mt-0.5 text-xs text-muted">{description}</p>}
          </div>
          <IconButton icon={X} label="Zamknij (Esc)" onClick={onClose} size="sm" />
        </header>

        <div className="max-h-[calc(100vh-16rem)] overflow-y-auto px-5 py-4">{children}</div>

        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-line bg-elevated px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body
  );
}

export default Modal;
